import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { HStack, Modal } from '@musinsa/mwms-ds'
import { PrimaryButton, RadioGroup, SecondaryButton, Textarea, Typography } from '@musinsa/layer-components-desktop'
import { Select } from '@/components/content/Select'
import styled from 'styled-components'
import { Icon } from '@musinsa/pbo-assets'
import { format, max, parseISO, startOfToday } from 'date-fns'
import { AxiosError } from 'axios'
import { checkExcelIsEmpty } from '@/utils/Excel'
import { DatePickerValue } from '@/components/content/DatePicker/DatePickerInput'
import { PeriodField } from '@/components/content/PeriodField'
import { useGetCutOffWarehouses } from '@/services/warehouse/useWareHouseQueries'
import {
  useCreateBundleCondition,
  useCreateBundleConditionSku,
  useGetBundleConditionByWarehouse,
  useUploadBundleConditionsExcel,
  useUploadBundleConditionsSkuExcel,
} from '@/services/outbound/singleBatchSetting/useSingleBatchSettingQueries'
import { SINGLE_PACK_SKU_AXIS_FLAG, UploadBundleConditionsExcelErrorData } from '@/services/outbound/singleBatchSetting/types'
import { useFeatureFlag } from '@/services/feature/useFeatureQueries'
import { tokens } from '@musinsa/layer-tokens/desktop'
import { formatTimeInput, isValidTime } from '@/pages/outbound/singleBatchSetting/utils/timeUtils'
import { parseDirectInput, validateBarcodes, validateSkuIds } from '@/pages/outbound/singleBatchSetting/utils/directInput'
import { Utils } from '@/utils/Utils'

type RegisterMode = 'excel' | 'direct' | 'all_product'


export interface SingleBatchSettingRegisterModalRef {
  triggerFileInput: () => void
}

interface Props {
  onClose: () => void
  onSuccess: () => void
  onPartialFailure: (failedRows: string, message?: string) => void
  onExcelFailure: (message?: string) => void
  onDirectFailure: (message: string) => void
  onFileReselected?: () => void
}

const SingleBatchSettingRegisterModal = forwardRef<SingleBatchSettingRegisterModalRef, Props>(function SingleBatchSettingRegisterModal(
  { onClose, onSuccess, onPartialFailure, onExcelFailure, onDirectFailure, onFileReselected },
  ref,
) {
  const [warehouseCode, setWarehouseCode] = useState<string | undefined>()
  const [startDate, setStartDate] = useState<DatePickerValue>(null)
  const [startTime, setStartTime] = useState('')
  const [endDate, setEndDate] = useState<DatePickerValue>(null)
  const [endTime, setEndTime] = useState('')
  const [mode, setMode] = useState<RegisterMode>('excel')
  const [directInput, setDirectInput] = useState('')
  const [excelFile, setExcelFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(ref, () => ({ triggerFileInput: () => fileInputRef.current?.click() }))

  const { options: allWarehouseOptions } = useGetCutOffWarehouses()
  const warehouseOptions = allWarehouseOptions.filter((o) => o.value !== 'JPWH01')
  const { data: existingCondition } = useGetBundleConditionByWarehouse(warehouseCode)
  // 로딩 중이거나 조회에 실패하면 false(barcode 축)다
  const { data: isSkuAxis = false } = useFeatureFlag(SINGLE_PACK_SKU_AXIS_FLAG)
  const keyLabel = isSkuAxis ? 'SKU ID' : '바코드'

  const clearPeriod = useCallback(() => {
    setStartDate(null)
    setStartTime('')
    setEndDate(null)
    setEndTime('')
  }, [])

  useEffect(() => {
    if (!warehouseCode || !existingCondition) {
      clearPeriod()
      return
    }
    setStartDate(parseISO(existingCondition.startAt))
    setStartTime(format(parseISO(existingCondition.startAt), 'HH:mm'))
    setEndDate(parseISO(existingCondition.endAt))
    setEndTime(format(parseISO(existingCondition.endAt), 'HH:mm'))
  }, [warehouseCode, existingCondition, clearPeriod])

  const { mutateAsync: createBundleCondition, isPending: isCreating } = useCreateBundleCondition()
  const { mutateAsync: createBundleConditionSku, isPending: isCreatingSku } = useCreateBundleConditionSku()
  const { mutateAsync: uploadBundleConditionsExcel, isPending: isUploading } = useUploadBundleConditionsExcel()
  const { mutateAsync: uploadBundleConditionsSkuExcel, isPending: isUploadingSku } = useUploadBundleConditionsSkuExcel()
  const isPending = isCreating || isCreatingSku || isUploading || isUploadingSku

  const keys = parseDirectInput(directInput)
  const keyError = mode !== 'direct' ? null : isSkuAxis ? validateSkuIds(keys) : validateBarcodes(keys)

  const currentStartAt = startDate && isValidTime(startTime) ? `${format(startDate as Date, 'yyyy-MM-dd')}T${startTime}:00` : ''
  const currentEndAt = endDate && isValidTime(endTime) ? `${format(endDate as Date, 'yyyy-MM-dd')}T${endTime}:00` : ''
  const isEndBeforeStart = !!currentEndAt && currentEndAt <= currentStartAt
  const isEndBeforeNow = !!currentEndAt && currentEndAt <= format(new Date(), "yyyy-MM-dd'T'HH:mm") + ':00'
  const endMinDate = startDate ? max([startOfToday(), startDate as Date]) : startOfToday()

  const isSubmitDisabled =
    isPending ||
    !warehouseCode ||
    !currentStartAt ||
    !currentEndAt ||
    isEndBeforeStart ||
    isEndBeforeNow ||
    (mode === 'direct' && (keys.length === 0 || !!keyError)) ||
    (mode === 'excel' && !excelFile)

  const handleWarehouseChange = (code: string) => {
    setMode('excel')
    setDirectInput('')
    setExcelFile(null)
    setWarehouseCode(code)
  }

  const handleModeChange = (v: string) => {
    setMode(v as RegisterMode)
    setDirectInput('')
    setExcelFile(null)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setExcelFile(file)
      onFileReselected?.()
    }
    e.target.value = ''
  }

  const handleRegister = useCallback(async () => {
    if (!startDate || !endDate || !warehouseCode) return
    const startAt = `${format(startDate as Date, 'yyyy-MM-dd')}T${startTime}:00`
    const endAt = `${format(endDate as Date, 'yyyy-MM-dd')}T${endTime}:00`

    try {
      if (mode === 'excel') {
        // 빈 엑셀(헤더 외 데이터 행 없음)은 BE 도달 전 사전 차단 (LOGISPRD-8729)
        try {
          if (await checkExcelIsEmpty(excelFile!)) {
            onExcelFailure(`${keyLabel}를 입력해주세요.`)
            return
          }
        } catch {
          // 파싱 실패는 BE 검증에 위임
        }
        const uploadExcel = isSkuAxis ? uploadBundleConditionsSkuExcel : uploadBundleConditionsExcel
        await uploadExcel({ file: excelFile!, startAt, endAt, warehouseCode })
      } else if (mode === 'direct' && isSkuAxis) {
        await createBundleConditionSku({ warehouseCode, startAt, endAt, skuIds: keys })
      } else if (mode === 'direct') {
        await createBundleCondition({ warehouseCode, conditionType: 'CENTER_BARCODE', startAt, endAt, barcodes: keys })
      } else {
        await createBundleCondition({ warehouseCode, conditionType: 'CENTER_ALL', startAt, endAt })
      }
      onSuccess()
    } catch (e) {
      const error = e as AxiosError<ApiResponseV2<UploadBundleConditionsExcelErrorData | null>>
      const isExcel = (error.config?.url ?? '').includes('/excel')

      if (!isExcel) {
        const message = error.response?.data?.meta?.message ?? '단수합포 설정 등록에 실패했습니다.'
        onDirectFailure(message)
        return
      }

      if (error.response?.status !== 400) {
        onExcelFailure(error.response?.data?.meta?.message)
        return
      }

      const errorRows = error.response.data?.data?.errorRows
      if (errorRows && errorRows.length > 0) {
        onPartialFailure(errorRows.join(', '), error.response?.data?.meta?.message)
      } else {
        onExcelFailure(error.response?.data?.meta?.message)
      }
    }
  }, [
    warehouseCode,
    startDate,
    startTime,
    endDate,
    endTime,
    mode,
    isSkuAxis,
    keyLabel,
    keys,
    excelFile,
    createBundleCondition,
    createBundleConditionSku,
    uploadBundleConditionsExcel,
    uploadBundleConditionsSkuExcel,
    onSuccess,
    onPartialFailure,
    onExcelFailure,
    onDirectFailure,
  ])

  const handleDownloadTemplate = useCallback(() => {
    const fileName = isSkuAxis ? '단수합포등록_템플릿_SKU.xlsx' : '단수합포등록_템플릿.xlsx'
    const a = document.createElement('a')
    a.href = Utils.assetUrl(fileName)
    a.download = fileName
    a.click()
  }, [isSkuAxis])

  return (
    <Modal open customWidth={640} hideFooter title="단수합포 설정" onClose={onClose}>
      <BodyContent>
        <InfoBanner>
          <Icon name="ic-infoCircle" size={20} filled color="#8a8a8a" />
          <Typography name="base-text-l-medium" colorToken="fg">
            {existingCondition
              ? '센터당 1개의 기간만 등록할 수 있습니다. (B2B 출고는 적용되지 않습니다.)'
              : '단수합포가 설정된 기간 동안 반영됩니다. (B2B 출고는 적용되지 않습니다.)'}
          </Typography>
        </InfoBanner>

        <FormSection>
          <FieldGroup>
            <FieldLabelRow>
              <Typography name="base-text-l-medium" colorToken="fg">
                센터
              </Typography>
              <Typography name="base-text-l-medium" colorToken="layerDesktop-fg-accent">
                (필수)
              </Typography>
            </FieldLabelRow>
            <Select
              value={warehouseCode}
              onValueChange={handleWarehouseChange}
              options={warehouseOptions}
              placeholder="선택"
              optionMaxHeight={300}
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabelRow>
              <Typography name="base-text-l-medium" colorToken="fg">
                기간
              </Typography>
              <Typography name="base-text-l-medium" colorToken="layerDesktop-fg-accent">
                (필수)
              </Typography>
            </FieldLabelRow>
            <PeriodRow>
              <PeriodField
                label="시작일시"
                date={startDate}
                time={startTime}
                onDateChange={setStartDate}
                onTimeChange={(e) => setStartTime(formatTimeInput(e.target.value))}
                datePlaceholder="시작일"
                timePlaceholder="시작시간"
                maxDate={(endDate as Date) ?? undefined}
                errorText={isEndBeforeStart ? '시작일시는 종료일시 이전으로 설정하세요.' : undefined}
              />
              <PeriodField
                label="종료일시"
                date={endDate}
                time={endTime}
                onDateChange={setEndDate}
                onTimeChange={(e) => setEndTime(formatTimeInput(e.target.value))}
                datePlaceholder="종료일"
                timePlaceholder="종료시간"
                minDate={endMinDate}
                errorText={
                  isEndBeforeStart
                    ? '종료일시는 시작일시보다 늦어야 합니다.'
                    : isEndBeforeNow
                      ? '종료일시는 현재 시간보다 늦어야 합니다.'
                      : undefined
                }
              />
            </PeriodRow>
          </FieldGroup>

          <FieldGroup>
            <FieldLabelRow>
              <Typography name="base-text-l-medium" colorToken="fg">
                {keyLabel}
              </Typography>
              <Typography name="base-text-l-medium" colorToken="layerDesktop-fg-accent">
                (필수)
              </Typography>
            </FieldLabelRow>
            <RadioGroup.Root value={mode} onValueChange={handleModeChange}>
              <RadioRow>
                <HStack gap={8}>
                  <RadioGroup.Item value="excel" size="small" />
                  <Typography name="base-text-l">엑셀업로드</Typography>
                </HStack>
                <HStack gap={8}>
                  <RadioGroup.Item value="direct" size="small" />
                  <Typography name="base-text-l">직접입력</Typography>
                </HStack>
                <HStack gap={8}>
                  <RadioGroup.Item value="all_product" size="small" />
                  <Typography name="base-text-l">{isSkuAxis ? '전체상품' : '전체바코드'}</Typography>
                </HStack>
              </RadioRow>
            </RadioGroup.Root>
            {mode === 'direct' && (
              <div>
                <Textarea
                  style={{ height: 104 }}
                  value={directInput}
                  onValueChange={setDirectInput}
                  validationState={keyError ? 'error' : 'none'}
                  placeholder={
                    isSkuAxis
                      ? `SKU ID를 줄바꿈 또는 쉼표로 구분하여 입력하세요\n예) S2608260000630\nS2608260000631\nS2608260000632`
                      : `바코드를 줄바꿈 또는 쉼표로 구분하여 입력하세요\n예) MABCT701/NA/2XL\nMABCT701/NA/L\nMABCT701/NA/M`
                  }
                />
                {keyError && <ErrorText>{keyError}</ErrorText>}
              </div>
            )}
            {mode === 'excel' && (
              <ExcelUploadArea>
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFileChange} />

                <UploadRow>
                  {excelFile ? (
                    <ExcelFileUploaded>
                      <Typography
                        name="base-text-l"
                        colorToken="fg"
                        style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {excelFile.name}
                      </Typography>
                      <CloseButton type="button" onClick={() => setExcelFile(null)}>
                        <Icon name="ic-closeCircle" size={20} filled color="#8a8a8a" />
                      </CloseButton>
                    </ExcelFileUploaded>
                  ) : (
                    <ExcelGuideBox>
                      <Typography name="base-text-l" colorToken="fg-low">
                        엑셀 양식에 맞춰 작성 후 업로드 해주세요
                      </Typography>
                    </ExcelGuideBox>
                  )}
                  <UploadActions>
                    <PrimaryButton
                      variant="filled"
                      size="large"
                      label="업로드"
                      prefixIcon="ic-upload"
                      onClick={() => fileInputRef.current?.click()}
                    />
                    <SecondaryButton
                      variant="outlined"
                      size="large"
                      label="양식 다운로드"
                      prefixIcon="ic-download"
                      onClick={handleDownloadTemplate}
                    />
                  </UploadActions>
                </UploadRow>
              </ExcelUploadArea>
            )}
          </FieldGroup>
        </FormSection>
      </BodyContent>

      <DialogFooter>
        <SecondaryButton variant="outlined" size="large" widthMode="compact" label="취소" onClick={onClose} />
        <PrimaryButton variant="filled" size="large" widthMode="compact" label="등록" disabled={isSubmitDisabled} onClick={handleRegister} />
      </DialogFooter>
    </Modal>
  )
})

const BodyContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const InfoBanner = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: var(--banner-info-bg, rgba(224, 224, 224, 0.2));
  border: 1px solid var(--banner-info-border, #ebebeb);
  border-radius: 4px;
  padding: 16px 12px;
`

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FieldLabelRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const RadioRow = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
`

const PeriodRow = styled.div`
  display: flex;
  gap: 8px;
`

const ExcelUploadArea = styled.div``

const ExcelFileUploaded = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 8px;
  padding: 12px;
  background: var(--select-bg, #ffffff);
  border: 1px solid var(--select-border, #e0e0e0);
  border-radius: 4px;
  min-height: 44px;
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
`

const UploadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const ExcelGuideBox = styled(HStack)`
  width: 100%;
  border-radius: 8px;
  padding: 12px;
  background-color: ${tokens['bg-disabled']};
`

const ErrorText = styled.p`
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 1.4;
  color: var(--fg-critical, #f31110);
`

const UploadActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`

const DialogFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  border-top: 1px solid var(--stroke-divider-low, rgba(138, 138, 138, 0.1));
  margin-top: 20px;
  padding-top: 16px;
`

export default SingleBatchSettingRegisterModal
